import '../../styles/restScreens.css';
// Was EngagementDetailModal — a popup. Val, 2026-07-15: "Clicking on an engagement
// produces a popup rather than a cascade drop-down." He is right, and it broke the platform's
// ONE interaction grammar: every list in IntelAgent opens in place (Pattern Library entry #1,
// `disclosure.inline-edit`), and WR-013 says drawer-not-modal. A popup also hides the list it
// came from, so you lose your place — the exact thing the cascade avoids.
import DocumentsPanel from '../documents/DocumentsPanel';
import AccountPaymentsPanel from '../payments/AccountPaymentsPanel';
import { supabaseClient } from '../../config/supabase';
import { useEffect, useState } from 'react';
import TripChangeReview from '../dispatch/TripChangeReview';
import TripMaskedLinePanel from '../dispatch/TripMaskedLinePanel';
import TripGroupManifest from '../dispatch/groupTransfers/TripGroupManifest';
import FlightRidePanel from '../events/foundation/FlightRidePanel';
import LifecycleBar from './LifecycleBar';
import { allowedNextStatuses, statusMeta } from './engagementConstants';
import { toneTextStyle, paymentStatusTone } from '../clients/clientsConstants';
import LiveTripTrackingPanel from '../tracking/LiveTripTrackingPanel';
import { useCompanyWords } from '../../context/CompanyWordsContext';

// Action-button styling per target status.
const ACTION_STYLE = {
    confirmed:   'bg-blue-600 hover:bg-blue-700 text-white',
    scheduled:   'bg-indigo-600 hover:bg-indigo-700 text-white',
    in_progress: 'bg-amber-600 hover:bg-amber-700 text-white',
    completed:   'bg-green-600 hover:bg-green-700 text-white',
    closed:      'bg-slate-700 hover:bg-slate-800 text-white',
    canceled:    'bg-red-600 hover:bg-red-700 text-white',
    no_show:     'bg-rose-600 hover:bg-rose-700 text-white'
};
// Statuses where a live tracker is meaningful — the same active set the driver
// app pushes location for (driver-app/src/screens/ActiveTripScreen.js).
const TRACKABLE_STATUSES = ['accepted', 'en_route', 'on_location', 'passenger_onboard'];

const ACTION_VERB = {
    confirmed: 'Confirm', scheduled: 'Mark Scheduled', in_progress: 'Start Trip',
    completed: 'Complete', closed: 'Close', canceled: 'Cancel', no_show: 'No-Show'
};

// Read-only detail view for one engagement: confirmation number front-and-center,
// lifecycle bar, all the who/what/where fields, and the legal next-step actions.
function EngagementDetailCard({ engagement, currentAssignment, entities = [], accounts = [], currentUserId, onClose, onEdit, onChangeStatus, onTripChanged, theme = 'light', liveDriver = null, canEdit = true, canShowTripPrices = true, canShowPaymentStatus = canShowTripPrices }) {
    const words = useCompanyWords();
    const [detailTab, setDetailTab] = useState('details');
    const dark = theme === 'dark';
    const [busyStatus, setBusyStatus] = useState(null);
    const [error, setError] = useState(null);
    const [cancelPair, setCancelPair] = useState(null);
    useEffect(() => { setCancelPair(null); }, [engagement?.id]);
    const [ownerName,setOwnerName]=useState('');
    useEffect(()=>{let active=true;setOwnerName('');
        if(engagement?.created_by)(async()=>{try{const {data,error}=await supabaseClient.from('user_profiles').select('full_name').eq('id',engagement.created_by).maybeSingle();if(active&&!error)setOwnerName(data?.full_name||'');}catch{/* RLS may withhold a former staff profile. */}})();
        return()=>{active=false;};
    },[engagement?.created_by]);


    if (!engagement) return null;

    const entity = entities.find((e) => e.id === engagement.entity_id);
    const account = accounts.find((a) => a.id === engagement.account_id);
    const meta = statusMeta(engagement.status);
    const nextStatuses = allowedNextStatuses(engagement.status);
    const isOwner = !currentUserId || engagement.created_by === currentUserId;

    const handleAction = async (target, cancelBoth) => {
        setError(null);
        setBusyStatus(target);
        try {
            if (target === 'canceled' && cancelBoth === undefined) {
                const { data: pair, error: failure } = await supabaseClient.from('m9_reservation_pairs')
                    .select('outbound_id,return_id').or(`outbound_id.eq.${engagement.id},return_id.eq.${engagement.id}`).maybeSingle();
                if (failure) throw failure;
                if (pair) { setCancelPair(pair); return; }
            }
            await onChangeStatus(target, { cancelBoth: cancelBoth === true });
            setCancelPair(null);
        } catch (e) {
            setError(e?.message || 'Could not update status.');
        } finally {
            // Always clear the busy flag — on success the modal re-renders with the next
            // step's buttons, and they must be clickable without closing/reopening.
            setBusyStatus(null);
        }
    };

    const money = (v) => (v == null || v === '' ? '—' : `$${Number(v).toFixed(2)}`);
    const text = (v) => (v == null || v === '' ? '—' : v);
    const when = (v) => (v ? new Date(v).toLocaleString() : '—');

    const cardCls = dark ? 'bg-gray-700/40 border-gray-700' : 'bg-gray-50 border-gray-200';
    const labelCls = dark ? 'text-gray-400' : 'text-gray-500';
    const valueCls = dark ? 'text-gray-100' : 'text-gray-800';

    const Field = ({ label, value, mono = false, valueStyle }) => (
        <div>
            <div className={`text-xs uppercase tracking-wide ${labelCls}`}>{label}</div>
            <div className={`mt-0.5 text-sm ${mono ? 'font-mono' : ''} ${valueCls}`} style={valueStyle}>{value}</div>
        </div>
    );

    return (
        <div data-m6-layout="engagements" data-m6-component="EngagementDetailCard" className="rounded-xl border border-[var(--surface-border)] bg-[var(--surface-raised)] overflow-hidden">
            <div className="flow-rule h-px w-full" />
            <div>
                {/* Header */}
                <div className={`sticky top-0 border-b px-6 py-4 flex items-center justify-between ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <h2 className={`text-xl font-bold ${'text-[var(--ink)]'}`}>{words.booking} Detail</h2>
                    <button onClick={onClose}
                        className="px-3 py-1 rounded-md text-xs font-medium border border-[var(--surface-border)] text-[var(--ink-muted)] hover:bg-[var(--surface-sunken)] transition">
                        Show less ▴
                    </button>
                </div>

                <nav className="m9-document-actions p-4" aria-label="Trip sections">{['details', 'documents', 'payments'].map((key) => <button className="m9-documents-pill" style={{ minHeight: 44, padding: '.6rem 1rem' }} aria-pressed={detailTab === key} key={key} onClick={() => setDetailTab(key)}>{key === 'details' ? 'Details' : key === 'payments' ? 'Payments' : 'Documents'}</button>)}</nav>
                {detailTab === 'payments' && <AccountPaymentsPanel client={supabaseClient} accountId={engagement.account_id} tripId={engagement.id} />}
                {detailTab === 'documents' && <div className="p-4"><DocumentsPanel client={supabaseClient} accountId={engagement.account_id} tripId={engagement.id} staff canEdit={canEdit} /></div>}
                <div hidden={detailTab !== 'details'} className="p-6 space-y-6">
                    {error && (
                        <div role="alert" className="p-3 rounded-lg bg-[var(--ui-alert)]/10 border border-[var(--ui-alert)]/30 text-[var(--ui-alert)] text-sm">{error}</div>
                    )}

                    <TripGroupManifest client={supabaseClient} tripId={engagement.id} />
                    <FlightRidePanel client={supabaseClient} tripId={engagement.id} allowLink hideWhenEmpty />
                    {/* Confirmation number — front and center */}
                    <div className={`rounded-xl border p-5 text-center ${dark ? 'bg-gray-900 border-gray-700' : 'bg-[var(--ui-orange)]/10 border-[var(--ui-orange)]/30'}`}>
                        <div className={`text-xs uppercase tracking-widest ${dark ? 'text-gray-400' : 'text-[var(--ui-orange)]'}`}>Confirmation Number</div>
                        {engagement.confirmation_number ? (
                            <div className="mt-1 font-mono text-3xl font-bold tracking-wider text-[var(--ink)]">
                                {engagement.confirmation_number}
                            </div>
                        ) : (
                            <div className={`mt-1 text-lg font-medium ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Not issued yet — appears when this engagement is confirmed.
                            </div>
                        )}
                        <div className="mt-3 flex items-center justify-center gap-2">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold border ${meta.pill}`}>
                                {meta.icon} {meta.label}
                            </span>
                        </div>
                    </div>

                    {/* Lifecycle bar */}
                    <div className={`rounded-xl border p-5 ${cardCls}`}>
                        <LifecycleBar status={engagement.status} theme={theme} />
                    </div>

                    {/* Trip fields */}
                    <div className={`rounded-xl border p-5 grid grid-cols-1 md:grid-cols-2 gap-4 ${cardCls}`}>
                        <Field label="Service Type" value={text(engagement.service_type)} />
                        <Field label="Hub" value={entity ? `${entity.entity_code}${entity.name ? ` — ${entity.name}` : ''}` : text(engagement.entity_id)} />
                        <Field label="Client" value={account ? account.account_name : text(engagement.account_id)} />
                        <Field label="Pickup Address" value={text(engagement.pickup_address)} />
                        <Field label="Dropoff Address" value={text(engagement.dropoff_address)} />
                        {(engagement.origin || !engagement.pickup_address) && <Field label="Origin" value={text(engagement.origin)} />}
                        {(engagement.destination || !engagement.dropoff_address) && <Field label="Destination" value={text(engagement.destination)} />}
                        <Field label="Passengers" value={text(engagement.passenger_count ?? engagement.reservation_details?.passengers ?? String(engagement.notes||'').match(/Passengers?:\s*(\d+)/i)?.[1])} />
                        <Field label="Luggage" value={text(engagement.luggage_count ?? engagement.reservation_details?.bags ?? String(engagement.notes||'').match(/(?:Luggage|Bags?):\s*(\d+)/i)?.[1])} />
                        <Field label="Flight" value={text(engagement.flight_info || engagement.reservation_details?.flight?.number || String(engagement.notes||'').match(/Flight:\s*([^\n]+)/i)?.[1])} />
                        {canShowTripPrices && <Field label="Quoted Amount" value={money(engagement.quoted_amount)} />}
                        {canShowTripPrices && <Field label="Final Amount" value={money(engagement.final_amount)} />}
                        {canShowPaymentStatus && <Field
                            label="Payment Status"
                            value={text(engagement.payment_status)}
                            valueStyle={toneTextStyle(paymentStatusTone(engagement.payment_status))}
                        />}
                        <Field label="Channel" value={text(engagement.channel)} />
                        {engagement.reservation_details?.flight?.flightTime && <Field label="Requested flight time"
                            value={`${engagement.reservation_details.flight.date || ''} ${engagement.reservation_details.flight.flightTime} (company time zone)`.trim()} />}
                        {engagement.reservation_details?.hours && <Field label="Charter hours" value={engagement.reservation_details.hours} />}
                        <div className="md:col-span-2">
                            <Field label="Notes" value={text(engagement.notes)} />
                        </div>
                    </div>

                    {currentAssignment && (
                        <div className={`rounded-xl border p-5 grid grid-cols-1 md:grid-cols-3 gap-4 ${cardCls}`}>
                            <Field label="Dispatch Status" value={text(currentAssignment.status)} />
                            <Field label="Driver ID" value={text(currentAssignment.driver_id)} mono />
                            <Field label="Vehicle ID" value={text(currentAssignment.vehicle_id)} mono />
                        </div>
                    )}

                    {/* BL-187 Gate 4 — live map + ETA. Rendered only while the trip
                        is actually moving: a live tracker on a completed or
                        cancelled trip shows a stale dot and invites the question
                        "why is my driver still on the map?". TRACKABLE_STATUSES is
                        the same active set the driver app pushes location for. */}
                    {currentAssignment && TRACKABLE_STATUSES.includes(currentAssignment.status) && (
                        <LiveTripTrackingPanel
                            assignmentId={currentAssignment.id}
                            driver={liveDriver}
                            theme={theme}
                        />
                    )}

                    {/* Masked trip comms: trip line, session open/closed, driver ↔ client call and text log. */}
                    <TripMaskedLinePanel engagementId={engagement.id} client={supabaseClient} canCall={Boolean(canEdit)} theme={theme} />

                    <TripChangeReview engagementId={engagement.id} canEdit={canEdit} onChanged={onTripChanged} />

                    {/* Owner / audit context */}
                    <div className={`rounded-xl border p-5 grid grid-cols-1 md:grid-cols-2 gap-4 ${cardCls}`}>
                        <Field label="Owner" value={<span>{engagement.reservation_details?.creation_actor?.name || ownerName || engagement.created_by_name || (isOwner ? 'You' : 'Staff member')}{!engagement.reservation_details?.creation_actor && ownerName && isOwner && <span className="ml-2 text-[var(--ui-positive)] font-medium">• you</span>}</span>} />
                        <Field label="Engagement ID" value={engagement.id} mono />
                        <Field label="Created" value={when(engagement.created_at)} />
                        <Field label="Last Updated" value={when(engagement.updated_at)} />
                        {engagement.series_id && (
                            <div className="md:col-span-2">
                                <Field label="Recurring Series" value={`Occurrence #${text(engagement.series_occurrence_index)} · ${text(engagement.series_occurrence_date)}`} mono />
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer — edit + lifecycle actions */}
                {cancelPair && canEdit && <div role="group" aria-label="Cancel both?" className="border-t p-4 space-x-3">
                    <p>This reservation has a linked return. Cancel both?</p>
                    <button disabled={Boolean(busyStatus)} onClick={() => handleAction('canceled', true)}>Cancel both reservations</button>
                    <button disabled={Boolean(busyStatus)} onClick={() => handleAction('canceled', false)}>Cancel this reservation only</button>
                    <button disabled={Boolean(busyStatus)} onClick={() => setCancelPair(null)}>Keep reservations</button>
                </div>}
                {canEdit && detailTab === 'details' && <div className={`sticky bottom-0 border-t px-6 py-4 flex flex-wrap items-center justify-between gap-3 ${'bg-[var(--surface-raised)] border-[var(--surface-border)]'}`}>
                    <button
                        onClick={() => onEdit(engagement)}
                        className={`px-4 py-2 rounded-lg font-medium border ${dark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                        ✏️ Edit
                    </button>

                    <div className="flex flex-wrap gap-2 justify-end">
                        {nextStatuses.length === 0 ? (
                            <span className={`text-sm ${labelCls}`}>No further status changes available.</span>
                        ) : (
                            nextStatuses.map((target) => (
                                <button
                                    key={target}
                                    onClick={() => handleAction(target)}
                                    disabled={!!busyStatus}
                                    className={`px-4 py-2 rounded-lg font-medium disabled:opacity-50 ${ACTION_STYLE[target] || 'bg-gray-600 text-white'}`}
                                >
                                    {busyStatus === target ? 'Working…' : `${statusMeta(target).icon} ${ACTION_VERB[target] || statusMeta(target).label}`}
                                </button>
                            ))
                        )}
                    </div>
                </div>}
                </div>
            </div>
    );
}

export default EngagementDetailCard;
